import { Dispatch, SetStateAction, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { DataImportHub as DataImportHubIcon, Upload } from '@mint-lib/icons';
import { Illustration } from '@mint-lib/illustrations';
import {
  Button,
  Divider,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerRoot,
  DrawerTitle,
  typographyVariants,
} from '@mint-lib/tw-components';
import { ProgressCircular } from '@mint-lib/tw-components/patterns';

import { useAiHubPermissions } from '../../../hooks/useAiHubPermissions.jsx';
import type { FileSystemNode } from '../../../types/fileSystem.js';
import { CheckboxGroup, type CheckboxItem } from './CheckboxGroup.jsx';
import { MobileUploadDrawer } from './MobileUploadDrawer.jsx';

type Props = {
  isDisabled: boolean;
  isOpen: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
  files: Array<FileSystemNode & CheckboxItem>;
  areFilesLoading: boolean;
  onItemChange: (
    fileName: string,
    checked: boolean,
    kind: FileSystemNode['kind'],
  ) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
};

const MobileFileSelectionContent = ({
  isDisabled,
  isOpen,
  onOpenChange,
  files,
  areFilesLoading,
  onItemChange,
  onSelectAll,
  onDeselectAll,
}: Props) => {
  const { t } = useTranslation('@mint-lib/ai-chat');
  const { canUploadKBFile } = useAiHubPermissions();

  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const onClose = async () => {
    setIsUploadOpen(false);
  };

  return (
    <>
      <Button
        icon
        variant="plain"
        disabled={isDisabled}
        onClick={() => onOpenChange(true)}
      >
        <DataImportHubIcon />
      </Button>
      <Drawer
        open={isOpen}
        onOpenChange={(open) => !open && onOpenChange(false)}
      >
        <DrawerPortal>
          <DrawerOverlay />
          <DrawerRoot
            side="bottom"
            aria-describedby={undefined}
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <DrawerHeader onClose={() => onOpenChange(false)}>
              <DrawerTitle>
                <div className="flex items-center gap-2">
                  {t('Knowledge base')}
                </div>
              </DrawerTitle>
            </DrawerHeader>
            <DrawerContent className="flex-col gap-4">
              {files.length ? (
                <>
                  <p
                    className={typographyVariants({
                      variant: 'bodyShort01',
                      color: 'secondary',
                    })}
                  >
                    {t('All selected items will be included in this request')}
                  </p>
                  <Divider />
                  {areFilesLoading ? (
                    <div className="flex h-37.5 items-center justify-center">
                      <ProgressCircular size="small" />
                    </div>
                  ) : (
                    <div className="max-h-[50vh] overflow-auto">
                      <CheckboxGroup
                        items={files}
                        onItemChange={onItemChange}
                        onSelectAll={onSelectAll}
                        onDeselectAll={onDeselectAll}
                      />
                    </div>
                  )}
                </>
              ) : (
                <div
                  aria-label="file-selection-empty"
                  className="flex h-full flex-col items-center justify-center text-center"
                >
                  <Illustration
                    component="IllustrationEmptyStateLight"
                    className="size-40"
                  />
                  <div className="flex flex-col gap-2">
                    <p
                      className={typographyVariants({
                        variant: 'h6',
                        color: 'primary',
                      })}
                    >
                      {t('The knowledge base is empty')}
                    </p>
                    <p
                      className={typographyVariants({
                        variant: 'bodyLong01',
                        color: 'primary',
                      })}
                    >
                      {t('Upload a file to use it with Mint IQ agents')}
                    </p>
                  </div>
                  <Button
                    className="mt-6"
                    variant="contained"
                    onClick={() => setIsUploadOpen(true)}
                    disabled={!canUploadKBFile}
                  >
                    <Upload />
                    {t('Upload')}
                  </Button>
                </div>
              )}
            </DrawerContent>
          </DrawerRoot>
        </DrawerPortal>
      </Drawer>

      <MobileUploadDrawer isOpen={isUploadOpen} onClose={onClose} />
    </>
  );
};

export { MobileFileSelectionContent };
